import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import type { Database } from "../lib/database.types";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

export interface Achievement {
  key: string;
  emoji: string;
  labelKey: string;
  descriptionKey: string;
  earned: boolean;
  progress: number;
  target: number;
}

export const ACHIEVEMENT_DEFINITIONS = {
  expertCAF: {
    key: "expertCAF",
    emoji: "🏆",
    labelKey: "achievement.expertCAF",
    descriptionKey: "achievement.expertCAF.description",
    target: 50,
    check: (profile: Profile) => (profile.correct_predictions || 0) >= 50,
    getProgress: (profile: Profile) => profile.correct_predictions || 0,
  },
  perfect10: {
    key: "perfect10",
    emoji: "🎯",
    labelKey: "achievement.perfect",
    descriptionKey: "achievement.perfect.description",
    target: 10,
    check: (profile: Profile) => (profile.perfect_predictions || 0) >= 10,
    getProgress: (profile: Profile) => profile.perfect_predictions || 0,
  },
  boldMaster: {
    key: "boldMaster",
    emoji: "🎲",
    labelKey: "achievement.boldMaster",
    descriptionKey: "achievement.boldMaster.description",
    target: 20,
    check: (profile: Profile) => (profile.bold_predictions || 0) >= 20,
    getProgress: (profile: Profile) => profile.bold_predictions || 0,
  },
  streak5: {
    key: "streak5",
    emoji: "🔥",
    labelKey: "achievement.streak",
    descriptionKey: "achievement.streak.description",
    target: 5,
    check: (profile: Profile) => (profile.prediction_streak || 0) >= 5,
    getProgress: (profile: Profile) => profile.prediction_streak || 0,
  },
};

export function useAchievements(userId?: string) {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAchievements = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Get user profile with achievement data
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;
      if (!profile) return;

      // Check each achievement
      const achievementList = Object.values(ACHIEVEMENT_DEFINITIONS).map(
        (def) => ({
          key: def.key,
          emoji: def.emoji,
          labelKey: def.labelKey,
          descriptionKey: def.descriptionKey,
          earned: def.check(profile),
          progress: def.getProgress(profile),
          target: def.target,
        })
      );

      setAchievements(achievementList);
    } catch (error) {
      console.error("Failed to fetch achievements:", error);
      setAchievements([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  return { achievements, loading, refetch: fetchAchievements };
}
